'use strict'

// A duration is the length of time between a start and an end, stored as a
// number in the unit the builder chose. Formulas add that number to a date
// with the same unit, so the builder does not convert hours into days by hand.

const UNITS = {
  seconds: 1000,
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000
}

const UNIT_LABELS = {
  seconds: ['second', 'seconds'],
  minutes: ['minute', 'minutes'],
  hours: ['hour', 'hours'],
  days: ['day', 'days']
}

const DURATION_UNIT_OPTIONS = [
  { value: 'seconds', text: 'seconds' },
  { value: 'minutes', text: 'minutes' },
  { value: 'hours', text: 'hours' },
  { value: 'days', text: 'days' }
]

function durationUnit (column) {

  const unit = column && column.durationUnit

  return Object.prototype.hasOwnProperty.call(UNITS, unit) ? unit : 'hours'
}

function durationFields (columnType, source) {

  if (columnType !== 'duration') return { durationUnit: null }

  return { durationUnit: durationUnit(source) }
}

function durationFieldKeys (uniqueTitle) {

  return [`${uniqueTitle}_start`, `${uniqueTitle}_end`]
}

function roundDuration (value) {

  return Math.round(value * 1e6) / 1e6
}

function durationBetween (start, end, unit) {

  const from = new Date(start).getTime()
  const to = new Date(end).getTime()

  if (!Number.isFinite(from) || !Number.isFinite(to)) return null

  return roundDuration((to - from) / UNITS[durationUnit({ durationUnit: unit })])
}

function addDuration (date, amount, unit) {

  const base = new Date(date).getTime()
  const length = Number(amount)
  const resolved = durationUnit({ durationUnit: unit })

  if (!Number.isFinite(base) || !Number.isFinite(length)) return null

  return new Date(base + length * UNITS[resolved]).toISOString()
}

function formatDuration (amount, unit) {

  if (amount == null || amount === '') return ''

  const length = typeof amount === 'number' ? amount : Number(String(amount).trim())

  if (!Number.isFinite(length)) return String(amount)

  const resolved = durationUnit({ durationUnit: unit })
  const text = String(roundDuration(length))
  const labels = UNIT_LABELS[resolved]
  const label = Math.abs(length) === 1 ? labels[0] : labels[1]

  return `${text} ${label}`
}

function parseDurationInput (value) {

  if (value == null || value === '') return { clear: true }

  if (typeof value === 'number') {

    return Number.isFinite(value) ? { mode: 'amount', amount: value } : { invalid: true }
  }

  if (typeof value === 'string') {

    const text = value.trim()

    if (!text) return { clear: true }

    if (text[0] === '{') {

      try {

        return parseDurationInput(JSON.parse(text))
      } catch (err) {

        return { invalid: true }
      }
    }

    const match = /^(-?\d+(?:\.\d+)?)(?:\s|$)/.exec(text)

    if (!match) return { invalid: true }

    return { mode: 'amount', amount: Number(match[1]) }
  }

  if (typeof value === 'object') {

    if (value.start != null || value.end != null) {

      return { mode: 'range', start: value.start, end: value.end }
    }

    if (value.amount != null || value.value != null) {

      return parseDurationInput(value.amount != null ? value.amount : value.value)
    }
  }

  return { invalid: true }
}

// Rewrites a duration column on a create or update body. A start and end
// become the length between them. A bare number is that length and leaves a
// previously stored start and end alone. `storeInstant` normalizes each
// endpoint the same way a date/time column is stored.
function applyDurationValues (columns, values, storeInstant) {

  if (!values || !Array.isArray(columns)) return values

  const store = storeInstant || ((value) => value)

  for (const column of columns) {

    if (!column || column.type !== 'duration' || !column.uniqueTitle) continue
    if (!Object.prototype.hasOwnProperty.call(values, column.uniqueTitle)) continue

    const key = column.uniqueTitle
    const parsed = parseDurationInput(values[key])

    if (parsed.clear) {

      values[key] = null
      values[`${key}_start`] = null
      values[`${key}_end`] = null
      continue
    }

    if (parsed.invalid) {

      throw new Error(`${column.title || key} needs a start and an end, or a number of ${durationUnit(column)}`)
    }

    if (parsed.mode === 'range') {

      const start = parsed.start == null || parsed.start === '' ? null : store(parsed.start)
      const end = parsed.end == null || parsed.end === '' ? null : store(parsed.end)

      values[`${key}_start`] = start
      values[`${key}_end`] = end
      values[key] = start && end ? durationBetween(start, end, durationUnit(column)) : null
      continue
    }

    values[key] = parsed.amount
  }

  return values
}

module.exports = {
  DURATION_UNIT_OPTIONS,
  durationUnit,
  durationFields,
  durationFieldKeys,
  durationBetween,
  addDuration,
  formatDuration,
  parseDurationInput,
  applyDurationValues
}
